import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowDownTrayIcon,
  PlusIcon,
  PhoneIcon,
  ClockIcon,
  UserIcon,
  XCircleIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
} from '../../lib/commonUtils';
import { toast } from 'react-toastify';
import useAppStore from '../../store/appStore';
import {
  RefreshButton,
  PageHeader,
  SearchAndFilters,
  KPISkeleton,
  TableSkeleton,
  getStatusBadge,
  formatStatus,
  StatCard,
} from '../../lib/commonUtils';

const getCreatedAt = (job) => {
  const iso = job?.local_record?.created_at;
  if (iso) {
    const d = new Date(iso);
    if (!Number.isNaN(d.getTime())) return d;
  }
  const unix = job?.elevenlabs_live_status?.created_at_unix;
  if (unix != null) {
    const n = Number(unix);
    const ms = n > 1e12 ? n : n * 1000;
    const d = new Date(ms);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
};

const BatchCallingList = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isFromUser = queryParams.get('fromUser') === 'true';

  const {
    batchCalling,
    fetchBatchCallingStatus,
    cancelBatchCalling,
    retryBatchCalling,
  } = useAppStore((s) => ({
    batchCalling: s.batchCalling,
    fetchBatchCallingStatus: s.fetchBatchCallingStatus,
    cancelBatchCalling: s.cancelBatchCalling,
    retryBatchCalling: s.retryBatchCalling,
  }));

  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [agentFilter, setAgentFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowLoading, setRowLoading] = useState({});
  const itemsPerPage = 20;

  const doRefresh = useCallback(async () => {
    setIsLoading(true);
    try {
      await fetchBatchCallingStatus?.();
    } finally {
      setIsLoading(false);
    }
  }, [fetchBatchCallingStatus]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setIsLoading(true);
      try {
        await fetchBatchCallingStatus?.();
      } finally {
        if (mounted) setIsLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [fetchBatchCallingStatus]);

  const jobs = batchCalling?.jobs || [];

  const agentOptions = useMemo(
    () => Array.from(new Set(jobs.map((j) => j.agent_name))).sort(),
    [jobs]
  );

  const filteredJobs = useMemo(() => {
    const term = (searchTerm || '').trim().toLowerCase();
    return jobs.filter((job) => {
      const call = String(job?.call_name || '').toLowerCase();
      const agent = String(job?.agent_name || '').toLowerCase();
      const matchesSearch = !term || call.includes(term) || agent.includes(term);
      const matchesStatus = statusFilter === 'all' || job.status === statusFilter;
      const matchesAgent = agentFilter === 'all' || job.agent_name === agentFilter;
      return matchesSearch && matchesStatus && matchesAgent;
    });
  }, [jobs, searchTerm, statusFilter, agentFilter]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentJobs = filteredJobs.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.max(1, Math.ceil(filteredJobs.length / itemsPerPage));

  const clearSearch = () => setSearchTerm('');
  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setAgentFilter('all');
    setCurrentPage(1);
  };

  const exportCsv = useCallback(() => {
    const header = ['Batch Job ID', 'Batch Name', 'Agent', 'Total Jobs', 'Status', 'Created At'];
    const rowsToUse = filteredJobs.length ? filteredJobs : jobs;
    const body = rowsToUse.map((j) => {
      const created = getCreatedAt(j);
      return [
        j.batch_job_id || '',
        j.call_name || '',
        j.agent_name || '',
        (j.total_numbers ?? j.local_record?.total_numbers ?? '') || '',
        j.status || '',
        created ? `${created.toLocaleDateString()} ${created.toLocaleTimeString()}` : '',
      ];
    });

    const csv = [header, ...body]
      .map((row) => row.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'batch-calling.csv';
    a.click();
    URL.revokeObjectURL(url);
  }, [filteredJobs, jobs]);

  const onCancelRow = async (job) => {
    const id = job.batch_job_id;
    const callName = String(job?.call_name || '').replace(/\s+/g, ' ').trim();
    if (!callName) return;
    setRowLoading((s) => ({ ...s, [id]: 'cancel' }));
    try {
      const res = await cancelBatchCalling(callName);
      if (res?.success) toast.success(`Batch '${callName}' cancelled`);
      else toast.error(res?.error || 'Failed to cancel');
    } finally {
      setRowLoading((s) => ({ ...s, [id]: '' }));
    }
  };

  const onRetryRow = async (job) => {
    const id = job.batch_job_id;
    const callName = String(job?.call_name || '').replace(/\s+/g, ' ').trim();
    if (!callName) return;
    setRowLoading((s) => ({ ...s, [id]: 'retry' }));
    try {
      const res = await retryBatchCalling(callName);
      if (res?.success) toast.success(`Batch '${callName}' retried`);
      else toast.error(res?.error || 'Failed to retry');
    } finally {
      setRowLoading((s) => ({ ...s, [id]: '' }));
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <PageHeader title="Batch Calling">
        <div className="block sm:hidden space-y-4 w-full">
          <div className="grid grid-cols-2 gap-3 w-full">
            <RefreshButton
              onClick={doRefresh}
              isLoading={isLoading}
              className="btn-secondary w-full py-3.5"
            />
            <button
              onClick={exportCsv}
              className="btn btn-secondary w-full py-3.5"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              Export
            </button>
          </div>
          {!isFromUser && (
            <Link
              to="/batchcalling/batchcallingform"
              className="btn btn-primary w-full py-4 text-sm"
            >
              <PlusIcon className="h-4 w-4" />
              Start New Batch Calling
            </Link>
          )}
        </div>

        <div className="hidden sm:flex items-center gap-2">
          <RefreshButton onClick={doRefresh} isLoading={isLoading} className="btn-secondary" />
          <button onClick={exportCsv} className="btn btn-secondary">
            <ArrowDownTrayIcon className="h-4 w-4" />
            Export CSV
          </button>
          {!isFromUser && (
            <Link
              to="/batchcalling/batchcallingform"
              className="btn btn-primary"
            >
              <PlusIcon className="h-4 w-4" />
              Start New Batch Calling
            </Link>
          )}
        </div>
      </PageHeader>

      <p className="text-sm text-white/40 font-medium italic">
        Manage and monitor batch calling jobs
        {batchCalling?.lastFetched && (
          <span className="ml-2 opacity-60">
            (last update {new Date(batchCalling.lastFetched).toLocaleString()})
          </span>
        )}
      </p>

      {isLoading ? (
        <KPISkeleton />
      ) : (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard title="Total Batches" value={filteredJobs.length} icon={PhoneIcon} />
          <StatCard title="Running" value={filteredJobs.filter((j) => j.status === 'in_progress').length} icon={ClockIcon} />
          <StatCard title="Pending" value={filteredJobs.filter((j) => j.status === 'pending').length} icon={UserIcon} />
          <StatCard title="Cancelled" value={filteredJobs.filter((j) => j.status === 'cancelled').length} icon={PhoneIcon} />
        </div>
      )}

      <div className="lg:hidden p-4 controlled-glass rounded-2xl mb-6 inner-highlight">
        <div className="space-y-4">
          <div className="relative group">
            <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-white/20 group-focus-within:text-white transition-colors" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              placeholder="Search batches..."
              className="input pl-11"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              className="input text-sm"
            >
              <option value="all" className="bg-[#1a1f35]">All Status</option>
              <option value="pending" className="bg-[#1a1f35]">Pending</option>
              <option value="in_progress" className="bg-[#1a1f35]">Running</option>
              <option value="completed" className="bg-[#1a1f35]">Completed</option>
              <option value="cancelled" className="bg-[#1a1f35]">Cancelled</option>
            </select>
            <select
              value={agentFilter}
              onChange={(e) => { setAgentFilter(e.target.value); setCurrentPage(1); }}
              className="input text-sm"
            >
              <option value="all" className="bg-[#1a1f35]">All Agents</option>
              {agentOptions.map((agent) => (
                <option key={agent} value={agent} className="bg-[#1a1f35]">{agent}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="hidden sm:block">
        <SearchAndFilters
          searchTerm={searchTerm}
          onSearchChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
          searchPlaceholder="Search batches..."
          filters={[
            {
              value: statusFilter,
              onChange: (e) => { setStatusFilter(e.target.value); setCurrentPage(1); },
              options: [
                { value: 'all', label: 'All Status' },
                { value: 'pending', label: 'Pending' },
                { value: 'in_progress', label: 'In Progress' },
                { value: 'completed', label: 'Completed' },
                { value: 'cancelled', label: 'Cancelled' },
              ],
            },
            {
              value: agentFilter,
              onChange: (e) => { setAgentFilter(e.target.value); setCurrentPage(1); },
              options: [
                { value: 'all', label: 'All Agents' },
                ...agentOptions.map((agent) => ({ value: agent, label: agent })),
              ],
            },
          ]}
        />
      </div>

      {isLoading ? (
        <TableSkeleton />
      ) : filteredJobs.length === 0 ? (
        <div className="card">
          <div className="flex flex-col items-center justify-center py-12 sm:py-14 text-center">
            <div className="rounded-full bg-primary-50 p-4 mb-4">
              <PhoneIcon className="h-8 w-8 text-primary-600" />
            </div>
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">No batch history yet</h3>
            <p className="text-xs sm:text-sm text-gray-500 mt-1 max-w-md">
              You don't have any batches yet. Try adjusting filters, or start a new batch to see results here.
            </p>
            <div className="mt-4 flex items-center gap-2">
              <button onClick={clearSearch} className="btn btn-secondary btn-sm">Clear Search</button>
              <button onClick={resetFilters} className="btn btn-secondary btn-sm">Reset Filters</button>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="glass-panel inner-highlight mb-6">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-white/5">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-6 py-4 sm:px-4 sm:py-2.5 text-left text-[10px] font-bold text-white/40 uppercase tracking-widest">Batch Name</th>
                    <th className="px-6 py-4 sm:px-4 sm:py-2.5 text-left text-[10px] font-bold text-white/40 uppercase tracking-widest">Agent</th>
                    <th className="px-6 py-4 sm:px-4 sm:py-2.5 text-left text-[10px] font-bold text-white/40 uppercase tracking-widest">Total Jobs</th>
                    <th className="px-6 py-4 sm:px-4 sm:py-2.5 text-left text-[10px] font-bold text-white/40 uppercase tracking-widest">Status</th>
                    <th className="px-6 py-4 sm:px-4 sm:py-2.5 text-left text-[10px] font-bold text-white/40 uppercase tracking-widest">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {currentJobs.map((job) => {
                    const isCancelable = ['pending', 'in_progress'].includes(job.status);
                    const loadingMode = rowLoading[job.batch_job_id];

                    return (
                      <tr key={job.batch_job_id} className="hover:bg-white/[0.02] transition-colors group">
                        <td className="px-6 py-4 sm:px-4 sm:py-2.5 text-sm font-bold text-white tracking-tight">{job.call_name}</td>
                        <td className="px-6 py-4 sm:px-4 sm:py-2.5 text-sm font-medium text-white/60">{job.agent_name}</td>
                        <td className="px-6 py-4 sm:px-4 sm:py-2.5 text-sm font-medium text-white/60">{job.total_numbers ?? job.local_record?.total_numbers ?? '—'}</td>
                        <td className="px-6 py-4 sm:px-4 sm:py-2.5">
                          <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${getStatusBadge(job.status)}`}>
                            {formatStatus(job.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 sm:px-4 sm:py-2.5">
                          {isCancelable ? (
                            <button
                              onClick={() => onCancelRow(job)}
                              disabled={loadingMode === 'cancel'}
                              className="text-red-400 hover:text-red-300 font-bold text-xs uppercase tracking-wider flex items-center gap-1 disabled:opacity-50"
                            >
                              <XCircleIcon className="h-4 w-4" />
                              {loadingMode === 'cancel' ? 'Wait...' : 'Cancel'}
                            </button>
                          ) : (
                            <button
                              onClick={() => onRetryRow(job)}
                              disabled={loadingMode === 'retry'}
                              className="text-amber-400 hover:text-amber-300 font-bold text-xs uppercase tracking-wider flex items-center gap-1 disabled:opacity-50"
                            >
                              <ArrowPathIcon className={`h-4 w-4 ${loadingMode === 'retry' ? 'animate-spin' : ''}`} />
                              {loadingMode === 'retry' ? 'Retrying...' : 'Retry'}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="controlled-glass rounded-2xl p-4 sm:p-3 flex flex-col sm:flex-row items-center justify-between gap-4 inner-highlight">
            <p className="text-[11px] font-bold text-white/30 uppercase tracking-widest">
              Showing <span className="text-white/60">{filteredJobs.length ? indexOfFirstItem + 1 : 0}</span> to{' '}
              <span className="text-white/60">{Math.min(indexOfLastItem, filteredJobs.length)}</span> of{' '}
              <span className="text-white/60">{filteredJobs.length}</span> results
            </p>
            <div className="flex items-center gap-2">
              <button
                className="btn py-2 px-4 sm:py-1.5 sm:px-3 text-xs rounded-xl sm:rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-20"
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <div className="w-8 h-8 sm:w-7 sm:h-7 rounded-lg bg-primary-500/10 border border-primary-500/20 flex items-center justify-center text-xs font-bold text-primary-400">
                {currentPage}
              </div>
              <button
                className="btn py-2 px-4 sm:py-1.5 sm:px-3 text-xs rounded-xl sm:rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-20"
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default BatchCallingList;